<script setup lang="ts">
import { createSpeedBump } from '@shared/createSpeedBump'
import type {
  HeroTextModule as HeroTextModuleProps,
} from '@consumer/types/serializers'
const {
  heroTextModule,
} = defineProps<{
  // The admin editor renders this same preview from its own serializer, which types
  // desktopHeading as `string | null` because the form writes null to clear it.
  heroTextModule: Omit<HeroTextModuleProps, 'desktopHeading'> & { desktopHeading?: string | null }
}>()

const backgroundColor = 'var(--section-bg-color, var(--white-100))'

onMounted(async () => {
  createSpeedBump('.hero-text-module-body')
})
const primaryTextColorVariable = computed(() => (heroTextModule.primaryTextColorName
  ? {
    '--primary-text-color-name': `var(${heroTextModule.primaryTextColorName})`,
  }
  : {}))
const secondaryTextColorVariable = computed(() => heroTextModule.secondaryTextColorName
  ? {
    '--secondary-text-color-name': `var(${heroTextModule.secondaryTextColorName})`,
  }
  : {},
)

</script>
<template>
  <div
    class="root"
    data-model-type="HeroTextModule"
    :style="{
      ...primaryTextColorVariable,
      ...secondaryTextColorVariable,
      '--hero-text-module-bg': `${backgroundColor}`,
    }"
  >
    <img
      v-if="
        heroTextModule.mobileImageUrl"
      alt=""
      :src="heroTextModule.mobileImageUrl"
      class="mobile-visible bg-image w-full object-cover absolute top-0 left-0 h-full"
      :style="{
        objectPosition:
          `${heroTextModule.mobileImageHorizontalAlignment} ${heroTextModule.mobileImageVerticalAlignment}`,
      }"
    >
    <img
      v-if="heroTextModule.desktopImageUrl"
      :src="heroTextModule.desktopImageUrl"
      alt=""
      class="desktop-visible bg-image w-full object-cover absolute top-0 left-0 h-full"
      :style="{
        objectPosition:
          `${heroTextModule.desktopImageHorizontalAlignment} ${heroTextModule.desktopImageVerticalAlignment}`,
      }"
    >
    <div
      class="hero-text-module font-sans relative"
    >
      <h2 class="heading">
        <span
          class="mobile-visible mobile-heading"
          v-html="heroTextModule.mobileHeading"
        />
        <span
          class="desktop-visible desktop-heading"
          v-html="heroTextModule.desktopHeading || heroTextModule.mobileHeading"
        />
      </h2>
      <div class="hero-text-module-body" v-html="heroTextModule.body"/>
    </div>
  </div>
</template>
<style scoped lang="scss">
.root {
  @apply relative;

  container-name: root;
  container-type: inline-size;
  isolation: isolate;

  /**
  we need to do this like so because apparently if you set a gradient
  background color it will treat it like a background image
  but if you have that background set as a flat color it will override other images
  so are unable to layer "background-image" then.
  to solve this we conditionally set a background if there are images present
  if we do it all the time then the background will override the image
  **/
  img {
    background: var(--hero-text-module-bg);
  }

  &:not(:has(img)) {
    background: var(--hero-text-module-bg);
  }
}

.mobile-visible {
  display: block;

  @container root (min-width: 1024px) {
    display: none;
  }
}

.desktop-visible {
  display: none;

  @container root (min-width: 1024px) {
    display: block;
  }
}

.hero-text-module {
  @apply flex flex-col;

  align-items: center;
  gap: 12px;
  padding: 24px;

  @container root (min-width: 1024px) {
    padding: 40px;
  }

  // needed for empty paragraph tags which tip tap renders with a br but
  // does not save https://github.com/ueberdosis/tiptap/issues/412
  @supports (height: 1lh) {
    :deep(p):empty {
      height: 1lh;
    }
  }

  @supports not (height: 1lh) {
    :deep(p):empty::after {
      content: "\00A0";
    }
  }

  .desktop-heading {
    font-size: 3.75rem;
    line-height: 100%;
  }

  > * {
    @apply text-align-center;

    text-wrap: balance;
  }

  .heading {
    color:
      var(
        --primary-text-color-name,
        var(--color-text-primary-dark)
      );
    font-size: 2rem;
    font-style: normal;
    font-weight: 600;
    line-height: 110%;
  }

  .hero-text-module-body {
    @apply text-balance font-normal text-base;

    color: var(--secondary-text-color-name, var(--color-text-secondary-dark));
    font-size: 1rem;
    font-weight: 500;
    line-height: 130%;
    max-width: 620px;

    @container root (min-width: 1024px) {
      font-size: 1.125rem;
      line-height: 140%;
    }

    :deep(a) {
      text-decoration: underline;
    }
  }
}
</style>
